import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, completePasswordMigration, db, deferPasswordMigration } from '../firebase';
import { MIN_PASSWORD_LENGTH } from '../constants/passwordPolicy';
import { userNeedsPasswordMigration } from '../utils/passwordMigration';

const PASSWORD_DEFER_DAYS = 7;

export default function SetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [deferBusy, setDeferBusy] = useState(false);
  const [error, setError] = useState('');

  const checkEligibility = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) {
      navigate('/login', { replace: true });
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'users', u.uid));
      const data = snap.data() || {};
      if (!userNeedsPasswordMigration(u, data)) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setStatus('ready');
    } catch (e) {
      console.error('SetPassword eligibility check failed:', e);
      setError('Could not load your account. Please try again.');
      setStatus('ready');
    }
  }, [navigate]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  const mapError = (err) => {
    const code = err?.code || '';
    if (code === 'auth/weak-password') {
      return `Choose a stronger password (at least ${MIN_PASSWORD_LENGTH} characters with upper, lower, number, and symbol).`;
    }
    if (code === 'auth/requires-recent-login') {
      return 'For security, sign out and sign in again with your email link, then return here to set your password.';
    }
    if (code === 'auth/too-many-requests') {
      return 'Too many attempts. Please wait a few minutes and try again.';
    }
    return err?.message || 'Could not save your password. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await completePasswordMigration(password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('SetPassword submit failed:', err?.code, err?.message || err);
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDefer = async () => {
    setError('');
    setDeferBusy(true);
    try {
      await deferPasswordMigration(PASSWORD_DEFER_DAYS);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('SetPassword defer failed:', err?.code, err?.message || err);
      setError(err?.message || 'Could not save your preference. Please try again.');
    } finally {
      setDeferBusy(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login', { replace: true });
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <p className="text-slate-600 text-sm">Checking your account…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound size={28} />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2 text-center">Set a password</h1>
        <p className="text-sm text-slate-600 mb-6 text-center">
          You signed in with an email link. Set a password now so you can sign in without waiting for
          another link. We&apos;ll keep email-link sign-in available until everyone has migrated.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label htmlFor="migration-password" className="block text-sm font-semibold text-slate-700 mb-1">
              New password
            </label>
            <input
              id="migration-password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              disabled={busy || deferBusy}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#019dad] focus:ring-2 focus:ring-[#019dad]/20"
            />
          </div>
          <div>
            <label htmlFor="migration-confirm-password" className="block text-sm font-semibold text-slate-700 mb-1">
              Confirm password
            </label>
            <input
              id="migration-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
              placeholder="Re-enter your password"
              disabled={busy || deferBusy}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#019dad] focus:ring-2 focus:ring-[#019dad]/20"
            />
          </div>
          <p className="text-xs text-slate-500 m-0">
            Use at least {MIN_PASSWORD_LENGTH} characters with upper, lower, number, and symbol.
          </p>
          {error && (
            <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3 m-0">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy || deferBusy}
            className="w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save password'}
          </button>
        </form>

        <div className="flex flex-col gap-2 mt-4">
          <button
            type="button"
            onClick={handleDefer}
            disabled={busy || deferBusy}
            className="w-full py-3 px-4 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50"
          >
            {deferBusy ? 'Saving…' : `Remind me in ${PASSWORD_DEFER_DAYS} days`}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full py-2 text-sm text-slate-500 flex items-center justify-center gap-2 hover:text-slate-700"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
